import { useState } from 'react';
import type { FormEvent, ReactNode } from 'react';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import { OtpInput } from '../components/OtpInput';
import { Seitenkopf } from '../components/Seitenkopf';
import { Button } from '../components/ui/Button';
import { Card } from '../components/ui/Card';
import { Input } from '../components/ui/Input';
import { IconMond, IconSonne, IconEinstellungen, IconHaken, IconHinweis } from '../components/icons';
import type { Darstellung, DarstellungSteuerung } from '../lib/darstellung';
import { cn } from '../lib/cn';
import { transition, uebergangSanft } from '../lib/motion';
import {
  DEMO,
  api,
  ApiFehler,
  type Benutzer,
  emailAendernAnfordern,
  emailAendernBestaetigen,
  passwortAendern,
  ueberallAbmelden,
} from '../lib/api';

const PASSWORT_MIN = 8;
const DEMO_CODE = '123456';

const DARSTELLUNG_OPTIONEN: Array<{ wert: Darstellung; label: string }> = [
  { wert: 'hell', label: 'Hell' },
  { wert: 'dunkel', label: 'Dunkel' },
  { wert: 'system', label: 'Systemeinstellung' },
];

export interface EinstellungenProps {
  benutzer: Benutzer;
  darstellung: DarstellungSteuerung;
  onAbmelden: () => void;
  /** Nach „Auf allen Geräten abmelden“: lokale Sitzung ist bereits beendet. */
  onUeberallAbgemeldet: () => void;
  /** Kontodaten neu laden (z. B. nach Änderung der E-Mail-Adresse). */
  onKontoAktualisieren: () => Promise<void>;
}

interface Pruefung {
  schluessel: string;
  titel: string;
  ergebnis: 'ok' | 'warnung';
  beschreibung: string;
}
interface Bericht {
  status: 'ok' | 'warnung';
  pruefungen: Pruefung[];
  zusammenfassung: Record<string, number>;
}

const DEMO_BERICHT: Bericht = {
  status: 'ok',
  pruefungen: [
    { schluessel: 'b', titel: 'Belege mit vollständiger Datei', ergebnis: 'ok', beschreibung: 'Zu jedem Beleg mit Datei ist der Inhalt vorhanden.' },
    { schluessel: 'p', titel: 'Prüfprotokolle mit vollständiger Datei', ergebnis: 'ok', beschreibung: 'Alle Protokoll-Dateien vorhanden.' },
    { schluessel: 'h', titel: 'Festgeschriebene Abrechnungen mit Nachweis', ergebnis: 'ok', beschreibung: 'Jede festgeschriebene Abrechnung trägt ihren Nachweis-Hash.' },
    { schluessel: 'z', titel: 'Saubere Zuordnung der Belege', ergebnis: 'ok', beschreibung: 'Jeder Beleg gehört zu einem Objekt dieses Mandanten.' },
  ],
  zusammenfassung: { objekte: 2, einheiten: 9, belege: 4, dateien: 3, anlagen: 4, abrechnungenFestgeschrieben: 1 },
};

const ZUS_LABEL: Record<string, string> = {
  objekte: 'Objekte',
  einheiten: 'Einheiten',
  mieter: 'Mieter',
  belege: 'Belege',
  dateien: 'Hinterlegte Dateien',
  anlagen: 'Anlagen',
  abrechnungenFestgeschrieben: 'Festgeschriebene Abrechnungen',
};

export function Einstellungen({
  benutzer,
  darstellung,
  onAbmelden,
  onUeberallAbgemeldet,
  onKontoAktualisieren,
}: EinstellungenProps) {
  const [sichert, setSichert] = useState(false);
  const [meldung, setMeldung] = useState<string | null>(null);
  const [prueft, setPrueft] = useState(false);
  const [bericht, setBericht] = useState<Bericht | null>(null);
  const [pruefFehler, setPruefFehler] = useState<string | null>(null);

  async function pruefen() {
    setPrueft(true);
    setPruefFehler(null);
    try {
      setBericht(DEMO ? DEMO_BERICHT : await api.get<Bericht>('/integritaet'));
    } catch (e) {
      setPruefFehler(e instanceof ApiFehler ? e.message : 'Die Prüfung ist fehlgeschlagen.');
    } finally {
      setPrueft(false);
    }
  }

  function ladeHerunter(inhalt: unknown) {
    const blob = new Blob([JSON.stringify(inhalt, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `grundpuls-sicherung-${new Date().toISOString().slice(0, 10)}.json`;
    a.click();
    URL.revokeObjectURL(url);
  }

  async function exportieren() {
    setSichert(true);
    setMeldung(null);
    try {
      if (DEMO) {
        ladeHerunter({
          _meta: {
            format: 'grundpuls-voll-export',
            hinweis: 'Vorschau — im Live-Betrieb wird hier Ihre vollständige Sicherung geladen.',
          },
        });
        setMeldung('Vorschau: Beispiel-Sicherung heruntergeladen.');
      } else {
        const daten = await api.get('/export/voll');
        ladeHerunter(daten);
        setMeldung('Sicherung wurde heruntergeladen.');
      }
    } catch (e) {
      setMeldung(e instanceof ApiFehler ? e.message : 'Der Export ist fehlgeschlagen.');
    } finally {
      setSichert(false);
    }
  }

  return (
    <div className="space-y-4">
      <Seitenkopf
        titel="Einstellungen"
        beschreibung="Zugang, Darstellung und Vorgaben für neue Abrechnungen."
      />

      <Zugang
        benutzer={benutzer}
        onAbmelden={onAbmelden}
        onUeberallAbgemeldet={onUeberallAbgemeldet}
        onKontoAktualisieren={onKontoAktualisieren}
      />

      <KontaktSupport />

      <Card
        titel="Darstellung"
        beschreibung="Die Auswahl wird auf diesem Gerät gespeichert."
        aktion={
          <Button
            variante="dezent"
            groesse="sm"
            onClick={darstellung.umschalten}
            aria-label={darstellung.istDunkel ? 'Helle Darstellung' : 'Dunkle Darstellung'}
            icon={darstellung.istDunkel ? <IconSonne groesse={18} /> : <IconMond groesse={18} />}
          />
        }
      >
        <div
          role="radiogroup"
          aria-label="Darstellung"
          className="grid grid-cols-1 gap-2 sm:grid-cols-3"
        >
          {DARSTELLUNG_OPTIONEN.map((o) => {
            const aktiv = darstellung.wahl === o.wert;
            return (
              <button
                key={o.wert}
                type="button"
                role="radio"
                aria-checked={aktiv}
                onClick={() => darstellung.setzeWahl(o.wert)}
                className={cn(
                  'rounded-lg border px-3 py-2.5 text-sm font-medium',
                  'transition-colors duration-fast ease-out',
                  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-focus focus-visible:ring-offset-2 focus-visible:ring-offset-surface',
                  aktiv
                    ? 'border-accent bg-accent-soft text-accent-soft-fg'
                    : 'border-border-strong text-fg-muted hover:bg-surface-2 hover:text-fg',
                )}
              >
                {o.label}
              </button>
            );
          })}
        </div>
      </Card>

      <Card
        titel="Vorgaben für Abrechnungen"
        beschreibung="Gelten für neu angelegte Abrechnungsläufe und lassen sich je Objekt überschreiben."
      >
        <dl className="space-y-3 text-sm">
          <Eintrag
            bezeichnung="Standard-Umlageschlüssel"
            wert="Wohnfläche (m²)"
            erlaeuterung="Gilt, solange im Mietvertrag nichts anderes vereinbart ist."
          />
          <Eintrag
            bezeichnung="Verbrauchsabhängiger Anteil Heizkosten"
            wert="70 %"
            erlaeuterung="Zulässiger Rahmen nach HeizkostenV: 50 bis 70 Prozent."
          />
          <Eintrag
            bezeichnung="CO₂-Kostenaufteilung"
            wert="Stufenmodell bis Abrechnungsjahr 2027"
            erlaeuterung="Ab Abrechnungsjahr 2028 feste Aufteilung 50/50."
          />
        </dl>
      </Card>

      <Card
        titel="Datensicherung"
        beschreibung="Alle Daten inklusive aller Belege und Dokumente jederzeit als eine Sicherungsdatei herunterladen."
        aktion={
          <Button onClick={exportieren} disabled={sichert}>
            {sichert ? 'Wird erstellt …' : 'Alle Daten exportieren'}
          </Button>
        }
      >
        <p className="text-sm text-fg-muted">
          Die Sicherung enthält Objekte, Einheiten, Mieter, Belege, Zähler, Anlagen, Abrechnungen
          und alle hinterlegten Dateien. Bewahren Sie sie sicher auf — sie enthält personenbezogene Daten.
        </p>
        {meldung && <p className="mt-2 text-sm font-medium text-success">{meldung}</p>}
      </Card>

      <Card
        titel="Datenprüfung"
        beschreibung="Prüfungssicher: kontrolliert, dass keine Datei fehlt, jede festgeschriebene Abrechnung ihren Nachweis trägt und alles sauber zugeordnet ist."
        aktion={
          <Button variante="sekundaer" onClick={pruefen} disabled={prueft}>
            {prueft ? 'Prüft …' : 'Jetzt prüfen'}
          </Button>
        }
      >
        {pruefFehler && <p className="text-sm font-medium text-danger">{pruefFehler}</p>}
        {bericht ? (
          <div className="space-y-3">
            <p
              className={
                'text-sm font-semibold ' + (bericht.status === 'ok' ? 'text-success' : 'text-warning-soft-fg')
              }
            >
              {bericht.status === 'ok' ? 'Alles in Ordnung — prüfungssicher.' : 'Es gibt Hinweise zu prüfen.'}
            </p>
            <ul className="space-y-2">
              {bericht.pruefungen.map((p) => (
                <li key={p.schluessel} className="flex items-start gap-2 text-sm">
                  <span className={'mt-0.5 shrink-0 ' + (p.ergebnis === 'ok' ? 'text-success' : 'text-warning-soft-fg')}>
                    {p.ergebnis === 'ok' ? <IconHaken groesse={16} /> : <IconHinweis groesse={16} />}
                  </span>
                  <span>
                    <span className="font-medium text-fg">{p.titel}</span>
                    <span className="text-fg-muted"> — {p.beschreibung}</span>
                  </span>
                </li>
              ))}
            </ul>
            <div className="flex flex-wrap gap-x-4 gap-y-1 border-t border-border pt-3 text-xs text-fg-subtle">
              {Object.entries(bericht.zusammenfassung).map(([k, v]) => (
                <span key={k}>
                  {ZUS_LABEL[k] ?? k}: <span className="font-medium text-fg">{v}</span>
                </span>
              ))}
            </div>
          </div>
        ) : (
          <p className="text-sm text-fg-muted">
            Noch nicht geprüft. Ein Klick zeigt den vollständigen Bestands- und Integritätsbericht.
          </p>
        )}
      </Card>

      <Card
        titel="Datenschutz"
        beschreibung="Mieterdaten werden ausschließlich zur Erstellung und Zustellung der Abrechnung verarbeitet."
      >
        <div className="flex items-start gap-3 text-sm text-fg-muted">
          <span className="mt-0.5 shrink-0 text-accent">
            <IconEinstellungen groesse={18} />
          </span>
          <p>
            Ein vollständiger Datenexport sowie die Löschung eines Mietverhältnisses stehen je
            Objekt zur Verfügung.
          </p>
        </div>
      </Card>
    </div>
  );
}

type Bereich = 'passwort' | 'email' | 'alle' | null;

/** Zugang: E-Mail-Adresse, Passwort, Abmelden — auch auf allen Geräten (Bauplan Abschnitt 9). */
function Zugang({
  benutzer,
  onAbmelden,
  onUeberallAbgemeldet,
  onKontoAktualisieren,
}: {
  benutzer: Benutzer;
  onAbmelden: () => void;
  onUeberallAbgemeldet: () => void;
  onKontoAktualisieren: () => Promise<void>;
}) {
  const reduziert = useReducedMotion();
  const [offen, setOffen] = useState<Bereich>(null);
  const [erfolg, setErfolg] = useState<string | null>(null);

  function umschalten(b: Bereich) {
    setErfolg(null);
    setOffen((o) => (o === b ? null : b));
  }

  function fertig(text: string) {
    setOffen(null);
    setErfolg(text);
    void onKontoAktualisieren();
  }

  return (
    <Card titel="Zugang" beschreibung="Anmeldedaten und Sitzungen Ihres Kontos.">
      <div className="divide-y divide-border">
        <Zeile
          titel="E-Mail-Adresse"
          wert={
            <span className="flex flex-wrap items-center gap-2">
              <span className="min-w-0 break-all">{benutzer.email}</span>
              {benutzer.googleVerknuepft && (
                <span className="rounded-pill bg-surface-2 px-2 py-0.5 text-xs font-medium text-fg-muted">
                  Mit Google verknüpft
                </span>
              )}
            </span>
          }
          knopf={offen === 'email' ? 'Schließen' : 'Ändern'}
          onKnopf={() => umschalten('email')}
        />
        <Aufklapp offen={offen === 'email'} reduziert={!!reduziert}>
          <EmailAendern onFertig={() => fertig('Die E-Mail-Adresse wurde geändert.')} />
        </Aufklapp>

        <Zeile
          titel="Passwort"
          wert={benutzer.passwortGesetzt ? '••••••••' : 'Nicht festgelegt — Anmeldung nur über Google'}
          knopf={offen === 'passwort' ? 'Schließen' : benutzer.passwortGesetzt ? 'Ändern' : 'Festlegen'}
          onKnopf={() => umschalten('passwort')}
        />
        <Aufklapp offen={offen === 'passwort'} reduziert={!!reduziert}>
          <PasswortAendern
            mitAltem={benutzer.passwortGesetzt}
            onFertig={() => fertig('Das Passwort wurde gespeichert. Alle anderen Geräte wurden abgemeldet.')}
          />
        </Aufklapp>
      </div>

      {erfolg && (
        <p className="mt-3 flex items-center gap-2 rounded-lg bg-success-soft px-3 py-2 text-sm text-success-soft-fg">
          <IconHaken groesse={16} /> {erfolg}
        </p>
      )}

      <div className="mt-4 flex flex-wrap gap-2 border-t border-border pt-4">
        <Button variante="sekundaer" onClick={onAbmelden}>
          Abmelden
        </Button>
        <Button variante="dezent" onClick={() => umschalten('alle')}>
          Auf allen Geräten abmelden
        </Button>
      </div>
      <Aufklapp offen={offen === 'alle'} reduziert={!!reduziert}>
        <UeberallAbmelden onAbbrechen={() => setOffen(null)} onFertig={onUeberallAbgemeldet} />
      </Aufklapp>
    </Card>
  );
}

function Zeile({
  titel,
  wert,
  knopf,
  onKnopf,
}: {
  titel: string;
  wert: ReactNode;
  knopf: string;
  onKnopf: () => void;
}) {
  return (
    <div className="flex items-center justify-between gap-3 py-3 first:pt-0">
      <div className="min-w-0">
        <p className="text-xs font-medium uppercase tracking-wide text-fg-subtle">{titel}</p>
        <div className="mt-0.5 text-sm text-fg">{wert}</div>
      </div>
      <Button variante="dezent" groesse="sm" onClick={onKnopf}>
        {knopf}
      </Button>
    </div>
  );
}

function Aufklapp({ offen, reduziert, children }: { offen: boolean; reduziert: boolean; children: ReactNode }) {
  return (
    <AnimatePresence initial={false}>
      {offen && (
        <motion.div
          initial={{ height: 0, opacity: 0 }}
          animate={{ height: 'auto', opacity: 1 }}
          exit={{ height: 0, opacity: 0 }}
          transition={transition(reduziert, uebergangSanft)}
          className="overflow-hidden"
        >
          <div className="pb-4 pt-1">{children}</div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

function Fehlerzeile({ text }: { text: string | null }) {
  if (!text) return null;
  return (
    <p role="alert" className="rounded-lg bg-danger-soft px-3 py-2 text-sm text-danger-soft-fg">
      {text}
    </p>
  );
}

function PasswortAendern({ mitAltem, onFertig }: { mitAltem: boolean; onFertig: () => void }) {
  const [alt, setAlt] = useState('');
  const [neu, setNeu] = useState('');
  const [neu2, setNeu2] = useState('');
  const [laeuft, setLaeuft] = useState(false);
  const [fehler, setFehler] = useState<string | null>(null);

  async function absenden(e: FormEvent) {
    e.preventDefault();
    if (mitAltem && !alt) return setFehler('Bitte das aktuelle Passwort angeben.');
    if (neu.length < PASSWORT_MIN) return setFehler(`Das Passwort muss mindestens ${PASSWORT_MIN} Zeichen lang sein.`);
    if (neu !== neu2) return setFehler('Die Passwörter stimmen nicht überein.');
    setFehler(null);
    setLaeuft(true);
    try {
      if (!DEMO) await passwortAendern(mitAltem ? alt : undefined, neu);
      onFertig();
    } catch (err) {
      setFehler(err instanceof ApiFehler ? err.message : 'Das Passwort konnte nicht gespeichert werden.');
    } finally {
      setLaeuft(false);
    }
  }

  return (
    <form className="space-y-3" onSubmit={absenden} noValidate>
      {mitAltem && (
        <Input
          label="Aktuelles Passwort"
          type="password"
          autoComplete="current-password"
          value={alt}
          onChange={(e) => setAlt(e.target.value)}
        />
      )}
      <Input
        label="Neues Passwort"
        type="password"
        autoComplete="new-password"
        hinweis={`Mindestens ${PASSWORT_MIN} Zeichen. Andere Geräte werden danach abgemeldet.`}
        value={neu}
        onChange={(e) => setNeu(e.target.value)}
      />
      <Input
        label="Neues Passwort wiederholen"
        type="password"
        autoComplete="new-password"
        value={neu2}
        onChange={(e) => setNeu2(e.target.value)}
      />
      <Fehlerzeile text={fehler} />
      <Button type="submit" ladend={laeuft}>
        Passwort speichern
      </Button>
    </form>
  );
}

function EmailAendern({ onFertig }: { onFertig: () => void }) {
  const [schritt, setSchritt] = useState<'adresse' | 'code'>('adresse');
  const [neu, setNeu] = useState('');
  const [code, setCode] = useState('');
  const [testCode, setTestCode] = useState<string | null>(null);
  const [laeuft, setLaeuft] = useState(false);
  const [fehler, setFehler] = useState<string | null>(null);

  async function codeAnfordern(e?: FormEvent) {
    e?.preventDefault();
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(neu.trim())) return setFehler('Bitte eine gültige E-Mail-Adresse angeben.');
    setFehler(null);
    setLaeuft(true);
    try {
      const r = DEMO ? { testCode: DEMO_CODE } : await emailAendernAnfordern(neu.trim());
      setTestCode(r.testCode ?? null);
      setCode('');
      setSchritt('code');
    } catch (err) {
      setFehler(err instanceof ApiFehler ? err.message : 'Der Code konnte nicht gesendet werden.');
    } finally {
      setLaeuft(false);
    }
  }

  async function bestaetigen(eingabe: string) {
    setFehler(null);
    setLaeuft(true);
    try {
      if (DEMO) {
        if (eingabe !== DEMO_CODE) throw new ApiFehler(400, 'Der Code ist ungültig oder abgelaufen.');
      } else {
        await emailAendernBestaetigen(eingabe);
      }
      onFertig();
    } catch (err) {
      setFehler(err instanceof ApiFehler ? err.message : 'Die Bestätigung ist fehlgeschlagen.');
      setCode('');
    } finally {
      setLaeuft(false);
    }
  }

  if (schritt === 'code') {
    return (
      <div className="space-y-3">
        <p className="text-sm leading-relaxed text-fg-muted">
          Ein sechsstelliger Code wurde an <span className="font-medium text-fg">{neu.trim()}</span> gesendet. Er ist
          15 Minuten gültig. Die Änderung wird erst mit dem Code wirksam.
        </p>
        <OtpInput
          wert={code}
          onWert={(w) => {
            setCode(w);
            if (fehler) setFehler(null);
          }}
          onVollstaendig={bestaetigen}
          fehler={!!fehler}
          gesperrt={laeuft}
          autoFokus
          ariaLabel="Bestätigungscode"
        />
        <Fehlerzeile text={fehler} />
        {testCode && (
          <p className="rounded-lg bg-accent-soft px-3 py-2 text-center text-sm text-accent-soft-fg">
            {DEMO ? 'Testzugang dieser Vorschau' : 'Testbetrieb ohne E-Mail-Versand'}: Code{' '}
            <span className="font-semibold">{testCode}</span>
          </p>
        )}
        <div className="flex flex-wrap gap-2">
          <Button variante="dezent" groesse="sm" onClick={() => void codeAnfordern()} disabled={laeuft}>
            Code erneut senden
          </Button>
          <Button variante="dezent" groesse="sm" onClick={() => setSchritt('adresse')} disabled={laeuft}>
            Andere Adresse
          </Button>
        </div>
      </div>
    );
  }

  return (
    <form className="space-y-3" onSubmit={codeAnfordern} noValidate>
      <Input
        label="Neue E-Mail-Adresse"
        type="email"
        autoComplete="email"
        inputMode="email"
        hinweis="Sie erhalten einen Bestätigungscode an die neue Adresse."
        value={neu}
        onChange={(e) => setNeu(e.target.value)}
      />
      <Fehlerzeile text={fehler} />
      <Button type="submit" ladend={laeuft}>
        Code senden
      </Button>
    </form>
  );
}

function UeberallAbmelden({ onAbbrechen, onFertig }: { onAbbrechen: () => void; onFertig: () => void }) {
  const [laeuft, setLaeuft] = useState(false);
  const [fehler, setFehler] = useState<string | null>(null);

  async function bestaetigen() {
    setLaeuft(true);
    setFehler(null);
    try {
      if (!DEMO) await ueberallAbmelden();
      onFertig();
    } catch (err) {
      setFehler(err instanceof ApiFehler ? err.message : 'Die Abmeldung ist fehlgeschlagen.');
      setLaeuft(false);
    }
  }

  return (
    <div className="mt-3 space-y-3 rounded-lg border border-border bg-surface-2 p-3">
      <p className="text-sm leading-relaxed text-fg">
        Alle Sitzungen dieses Kontos werden beendet — auf jedem Gerät und in jedem Browser, auch hier. Danach ist eine
        neue Anmeldung erforderlich.
      </p>
      <Fehlerzeile text={fehler} />
      <div className="flex flex-wrap gap-2">
        <Button variante="gefahr" onClick={bestaetigen} ladend={laeuft}>
          Alle Sitzungen beenden
        </Button>
        <Button variante="dezent" onClick={onAbbrechen} disabled={laeuft}>
          Abbrechen
        </Button>
      </div>
    </div>
  );
}

function Eintrag({
  bezeichnung,
  wert,
  erlaeuterung,
}: {
  bezeichnung: string;
  wert: string;
  erlaeuterung: string;
}) {
  return (
    <div className="border-b border-border pb-3 last:border-0 last:pb-0">
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <dt className="font-medium text-fg">{bezeichnung}</dt>
        <dd className="text-fg">{wert}</dd>
      </div>
      <p className="mt-1 text-xs text-fg-muted">{erlaeuterung}</p>
    </div>
  );
}

function KontaktSupport() {
  const [betreff, setBetreff] = useState('');
  const [nachricht, setNachricht] = useState('');
  const [sendet, setSendet] = useState(false);
  const [gesendet, setGesendet] = useState(false);
  const [fehler, setFehler] = useState<string | null>(null);

  async function senden() {
    setFehler(null);
    if (!betreff.trim() || !nachricht.trim()) {
      setFehler('Bitte Betreff und Nachricht ausfüllen.');
      return;
    }
    setSendet(true);
    try {
      if (!DEMO) {
        await api.post('/support', { betreff: betreff.trim(), nachricht: nachricht.trim() });
      }
      setGesendet(true);
      setBetreff('');
      setNachricht('');
      setTimeout(() => setGesendet(false), 4000);
    } catch (e) {
      setFehler(e instanceof ApiFehler ? e.message : 'Die Anfrage konnte nicht gesendet werden.');
    } finally {
      setSendet(false);
    }
  }

  return (
    <Card titel="Kontakt & Support" beschreibung="Ihre Anfrage geht direkt an unser Team. Wir melden uns per E-Mail.">
      {gesendet ? (
        <div className="flex items-center gap-2 rounded-lg border border-success bg-success-soft px-3 py-3 text-sm text-success-soft-fg">
          <IconHaken groesse={18} /> Vielen Dank — Ihre Anfrage ist eingegangen.
        </div>
      ) : (
        <div className="space-y-3">
          <Input label="Betreff" placeholder="Worum geht es?" value={betreff} onChange={(e) => setBetreff(e.target.value)} />
          <label className="block">
            <span className="mb-1.5 block text-sm font-medium text-fg">Nachricht</span>
            <textarea
              className="min-h-[110px] w-full rounded-lg border border-border bg-surface px-3 py-2 text-sm text-fg outline-none focus:border-accent"
              placeholder="Beschreiben Sie Ihr Anliegen möglichst genau."
              value={nachricht}
              onChange={(e) => setNachricht(e.target.value)}
            />
          </label>
          {fehler && <p className="text-sm font-medium text-danger">{fehler}</p>}
          <Button onClick={senden} disabled={sendet}>
            {sendet ? 'Wird gesendet …' : 'Anfrage senden'}
          </Button>
        </div>
      )}
    </Card>
  );
}

export default Einstellungen;
